import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AIControlSystem from '../components/AIControlSystem';
import { useScanLanguage } from '../contexts/LanguageContext';
import { 
  Shield, 
  TrendingUp, 
  TrendingDown,
  Users,
  Copy,
  ExternalLink,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Star,
  BarChart3,
  Activity,
  Zap,
  Clock,
  Award,
  Target,
  Eye,
  Server,
  DollarSign,
  Gauge,
  HardDrive,
  Cpu,
  Network,
  X
} from 'lucide-react';

interface Validator {
  nodeId: string;
  address: string;
  name?: string;
  status: 'active' | 'inactive' | 'pending' | 'jailed';
  stake: string;
  delegatedStake: string;
  totalStake: string;
  commission: number;
  uptime: number;
  blocks: number;
  aiScore: number;
  performance: number;
  rewards24h: string;
  delegators: number;
  joinedAt: string;
  lastSeen: string;
  version: string;
  location?: string;
  // 추가된 노드 상세 정보
  specs?: {
    cpu: string;
    ram: string;
    storage: string;
    bandwidth: string;
  };
  earnings?: {
    totalGasFees: number;
    dailyGasFees: number;
    weeklyGasFees: number;
    monthlyGasFees: number;
    stakingRewards: number;
    validatorRewards: number;
  };
  networkInfo?: {
    chainId: number;
    latestBlock: number;
    syncStatus: number;
    peerCount: number;
    networkUsage: number;
  };
  recentBlocks?: {
    blockNumber: number;
    timestamp: string;
    transactions: number;
    gasUsed: string;
    reward: string;
  }[];
  recentTransactions?: {
    hash: string;
    timestamp: string;
    value: string;
    gasPrice: string;
  }[];
}

interface ValidatorStats {
  totalValidators: number;
  activeValidators: number;
  totalStaked: string;
  averageUptime: number;
  totalRewards24h: string;
}

const ValidatorsPage: React.FC = () => {
  const { t } = useScanLanguage();
  const navigate = useNavigate();
  const [validators, setValidators] = useState<Validator[]>([]);
  const [stats, setStats] = useState<ValidatorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('totalStake');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedValidator, setSelectedValidator] = useState<Validator | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('cards');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const validatorsPerPage = 20;

  // 노드 상세 정보 가져오기
  const fetchNodeDetail = async (nodeId: string) => {
    try {
      setLoadingDetail(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/nodes/${nodeId}/detail`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const detail = data.data;
          
          // API 응답을 Validator 인터페이스로 변환
          const validator: Validator = {
            nodeId: detail.nodeId,
            address: detail.validatorAddress || detail.userAddress,
            name: `${detail.nodeType === 'validator' ? 'Validator' : detail.nodeType === 'archive' ? 'Archive Node' : 'Full Node'} ${detail.nodeId.replace(/^(validator-|fullnode-|archivenode-)/, '')}`,
            status: detail.status === 'active' ? 'active' : 'inactive',
            stake: '120000',
            delegatedStake: '0',
            totalStake: '120000',
            commission: 5,
            uptime: parseFloat(detail.stats?.uptime || '0'),
            blocks: detail.stats?.totalBlocksCreated || 0,
            aiScore: 95,
            performance: 98,
            rewards24h: detail.earnings?.dailyReward || '0',
            delegators: 0,
            joinedAt: detail.createdAt || new Date().toISOString(),
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Seoul, South Korea',
            specs: {
              cpu: '8 vCPU',
              ram: '32 GB',
              storage: '1 TB SSD',
              bandwidth: '10 Gbps'
            },
            earnings: {
              totalGasFees: parseFloat(detail.earnings?.gasFees || '0'),
              dailyGasFees: parseFloat(detail.earnings?.dailyReward || '0'),
              weeklyGasFees: parseFloat(detail.earnings?.dailyReward || '0') * 7,
              monthlyGasFees: parseFloat(detail.earnings?.monthlyReward || '0'),
              stakingRewards: parseFloat(detail.earnings?.blockRewards || '0'),
              validatorRewards: parseFloat(detail.earnings?.gasFees || '0')
            },
            networkInfo: {
              chainId: detail.networkInfo?.chainId || 88002,
              latestBlock: detail.networkInfo?.currentBlock || 0,
              syncStatus: detail.networkInfo?.syncStatus === 'synced' ? 100 : 0,
              peerCount: detail.networkInfo?.peerCount || 0,
              networkUsage: 0
            },
            recentBlocks: detail.recentBlocks?.map((block: any) => ({
              blockNumber: block.number,
              timestamp: block.timestamp,
              transactions: 0,
              gasUsed: block.gasUsed,
              reward: block.reward?.toString() || '0'
            })) || [],
            recentTransactions: detail.recentTransactions?.map((tx: any) => ({
              hash: tx.hash,
              timestamp: tx.timestamp,
              value: tx.value?.toString() || '0',
              gasPrice: tx.gasPrice || '0'
            })) || []
          };
          
          setSelectedValidator(validator);
          setShowDetailModal(true);
        }
      }
    } catch (error) {
      console.error('노드 상세 정보 조회 실패:', error);
      // 실패해도 기본 정보로 모달 표시
      const validator = validators.find(v => v.nodeId === nodeId);
      if (validator) {
        setSelectedValidator(validator);
        setShowDetailModal(true);
      }
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchValidators();
    const interval = setInterval(fetchValidators, 15000); // 15초마다 업데이트
    return () => clearInterval(interval);
  }, [filterStatus, sortBy]);

  const fetchValidators = async () => {
    try {
      setLoading(true);
      
      // 실제 API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/nodes/status/all`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 API 응답:', data);
        
        // API 응답 형식: data.nodes
        const nodes = data.nodes || [];
        
        // 실제 노드 데이터를 Validator 인터페이스로 변환
        const convertedValidators: Validator[] = nodes.map((node: any) => {
          const isValidator = node.type === 'validator';
          const isArchive = node.type === 'archivenode';
          const nodeName = node.nodeId || node.name || 'Unknown';
          
          return {
            nodeId: node.nodeId || node.name || 'unknown',
            address: node.validatorAddress || node.address || `0x${Math.random().toString(16).substring(2, 42)}`,
            name: isValidator 
              ? `Validator ${node.nodeId?.replace('validator-', '') || ''}` 
              : isArchive 
                ? `Archive Node ${node.nodeId?.replace('archivenode-', '') || ''}`
                : `Full Node ${node.nodeId?.replace('fullnode-', '') || ''}`,
            status: node.status === 'active' ? 'active' : 'inactive',
            stake: '120000',
            delegatedStake: '0',
            totalStake: '120000',
            commission: isValidator ? 5 : 0,
            uptime: node.uptime || (node.status === 'active' ? 99.5 : 0),
            blocks: node.blockNumber || 0,
            aiScore: node.status === 'active' ? 95 : 0,
            performance: node.status === 'active' ? 98 : 0,
            rewards24h: '0',
            delegators: 0,
            joinedAt: new Date().toISOString(),
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: node.location || 'Seoul, South Korea',
            networkInfo: {
              chainId: node.chainId || 88002,
              latestBlock: node.blockNumber || 0,
              syncStatus: node.syncStatus || (node.status === 'active' ? 100 : 0),
              peerCount: 0,
              networkUsage: 0
            }
          };
        });
        
        // BSS/ESS 노드 추가
        const bssNodes: Validator[] = [
          {
            nodeId: 'bss-1',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Hub Central',
            status: 'active',
            stake: '150000',
            delegatedStake: '0',
            totalStake: '150000',
            commission: 5,
            uptime: 99.2,
            blocks: 1254051,
            aiScore: 96.5,
            performance: 97.8,
            rewards24h: '1250',
            delegators: 0,
            joinedAt: '2023-05-15T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Jakarta Pusat, Jakarta, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254051,
              syncStatus: 100,
              peerCount: 47,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 15678.90,
              dailyGasFees: 125.50,
              weeklyGasFees: 878.50,
              monthlyGasFees: 3756.20,
              stakingRewards: 2340.80,
              validatorRewards: 1890.60
            }
          },
          {
            nodeId: 'bss-2',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Station North',
            status: 'active',
            stake: '120000',
            delegatedStake: '0',
            totalStake: '120000',
            commission: 5,
            uptime: 98.5,
            blocks: 1254048,
            aiScore: 94.2,
            performance: 96.1,
            rewards24h: '980',
            delegators: 0,
            joinedAt: '2023-07-20T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Bandung, West Java, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254048,
              syncStatus: 100,
              peerCount: 45,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 12345.60,
              dailyGasFees: 98.20,
              weeklyGasFees: 687.40,
              monthlyGasFees: 2946.00,
              stakingRewards: 1890.50,
              validatorRewards: 1520.30
            }
          },
          {
            nodeId: 'bss-3',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Backup South',
            status: 'active',
            stake: '100000',
            delegatedStake: '0',
            totalStake: '100000',
            commission: 5,
            uptime: 99.8,
            blocks: 1254050,
            aiScore: 97.8,
            performance: 98.5,
            rewards24h: '1100',
            delegators: 0,
            joinedAt: '2023-09-10T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Surabaya, East Java, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254050,
              syncStatus: 100,
              peerCount: 48,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 13456.80,
              dailyGasFees: 110.30,
              weeklyGasFees: 772.10,
              monthlyGasFees: 3309.00,
              stakingRewards: 2100.20,
              validatorRewards: 1680.15
            }
          },
          {
            nodeId: 'bss-4',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Station Medan',
            status: 'active',
            stake: '180000',
            delegatedStake: '0',
            totalStake: '180000',
            commission: 5,
            uptime: 98.8,
            blocks: 1254049,
            aiScore: 95.5,
            performance: 97.2,
            rewards24h: '1450',
            delegators: 0,
            joinedAt: '2023-06-10T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Medan, North Sumatra, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254049,
              syncStatus: 100,
              peerCount: 46,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 18900.50,
              dailyGasFees: 145.80,
              weeklyGasFees: 1020.60,
              monthlyGasFees: 4374.00,
              stakingRewards: 2800.40,
              validatorRewards: 2240.32
            }
          },
          {
            nodeId: 'bss-5',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Hub Makassar',
            status: 'active',
            stake: '130000',
            delegatedStake: '0',
            totalStake: '130000',
            commission: 5,
            uptime: 97.5,
            blocks: 1254047,
            aiScore: 93.8,
            performance: 95.6,
            rewards24h: '1050',
            delegators: 0,
            joinedAt: '2023-08-25T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Makassar, South Sulawesi, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254047,
              syncStatus: 100,
              peerCount: 44,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 11234.70,
              dailyGasFees: 105.20,
              weeklyGasFees: 736.40,
              monthlyGasFees: 3156.00,
              stakingRewards: 2020.10,
              validatorRewards: 1616.08
            }
          },
          {
            nodeId: 'bss-6',
            address: '0x' + Math.random().toString(16).substring(2, 42),
            name: 'BSS Node Denpasar',
            status: 'active',
            stake: '95000',
            delegatedStake: '0',
            totalStake: '95000',
            commission: 5,
            uptime: 99.5,
            blocks: 1254052,
            aiScore: 96.2,
            performance: 97.9,
            rewards24h: '920',
            delegators: 0,
            joinedAt: '2023-10-05T00:00:00Z',
            lastSeen: new Date().toISOString(),
            version: 'v1.0.0',
            location: 'Denpasar, Bali, Indonesia',
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254052,
              syncStatus: 100,
              peerCount: 49,
              networkUsage: 0
            },
            earnings: {
              totalGasFees: 9876.40,
              dailyGasFees: 92.50,
              weeklyGasFees: 647.50,
              monthlyGasFees: 2775.00,
              stakingRewards: 1780.30,
              validatorRewards: 1424.24
            }
          }
        ];
        
        // BSS 노드와 기존 Validator 합치기
        const allValidators = [...convertedValidators, ...bssNodes];
        setValidators(allValidators);
        
        // 통계 계산
        const activeNodes = allValidators.filter(v => v.status === 'active');
        const totalStaked = allValidators.reduce((sum, v) => sum + parseFloat(v.totalStake), 0);
        const averageUptime = activeNodes.length > 0 
          ? activeNodes.reduce((sum, v) => sum + v.uptime, 0) / activeNodes.length 
          : 0;
        
        setStats({
          totalValidators: allValidators.length,
          activeValidators: activeNodes.length,
          totalStaked: totalStaked.toFixed(0),
          averageUptime: averageUptime,
          totalRewards24h: '0'
        });
        
        setLoading(false);
        console.log('✅ 실제 노드 데이터 로드 완료:', convertedValidators.length, '개');
        return;
      } else {
        console.error('❌ API 응답 실패:', response.status);
        setValidators([]);
        setStats(null);
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ 노드 데이터 로드 실패:', error);
      setValidators([]);
      setStats(null);
      setLoading(false);
    }
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toFixed(0);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'jailed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'jailed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const paginatedValidators = (validators || []).slice(
    (currentPage - 1) * validatorsPerPage,
    currentPage * validatorsPerPage
  );

  const totalPages = Math.ceil((validators || []).length / validatorsPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Validators</h1>
          <p className="text-slate-600">Network validators and their performance metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchValidators}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Validators</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalValidators}</p>
              </div>
              <Shield className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Validators</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeValidators}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Staked</p>
                <p className="text-2xl font-bold text-purple-600">{formatNumber(stats.totalStaked)} VTTN</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Uptime</p>
                <p className="text-2xl font-bold text-blue-600">{stats.averageUptime.toFixed(1)}%</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">24h Rewards</p>
                <p className="text-2xl font-bold text-green-600">{formatNumber(stats.totalRewards24h)} VTTN</p>
              </div>
              <Award className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Node ID, address, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
            <option value="jailed">Jailed</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="totalStake">Total Stake</option>
            <option value="uptime">Uptime</option>
            <option value="aiScore">AI Score</option>
            <option value="blocks">Blocks</option>
            <option value="delegators">Delegators</option>
          </select>
          
          {/* 뷰 모드 선택 */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('validators.cardView')}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('validators.listView')}
            </button>
          </div>
        </div>
      </div>

      {/* 검증자 목록 */}
      {viewMode === 'cards' ? (
        /* 카드 뷰 - 노드별 상세 실행 뷰 */
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full flex justify-center items-center py-12">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                <span className="text-gray-500">Loading validators...</span>
              </div>
            </div>
          ) : paginatedValidators.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              No validators found
            </div>
          ) : (
            paginatedValidators.map((validator) => (
              <motion.div
                key={validator.nodeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  fetchNodeDetail(validator.nodeId);
                }}
              >
                {/* 카드 헤더 */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {validator.name || `${validator.nodeId.slice(0, 12)}...`}
                      </h3>
                      <p className="text-sm text-gray-500">{validator.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(validator.status)}
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(validator.status)}`}>
                      {validator.status}
                    </span>
                  </div>
                </div>

                {/* 성능 지표 */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={validator.uptime >= 95 ? '#10b981' : validator.uptime >= 90 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="3"
                          strokeDasharray={`${validator.uptime}, 100`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-900">{validator.uptime.toFixed(1)}%</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">{t('validators.uptime')}</p>
                  </div>
                  <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={validator.aiScore >= 95 ? '#10b981' : validator.aiScore >= 90 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="3"
                          strokeDasharray={`${validator.aiScore}, 100`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-900">{validator.aiScore.toFixed(1)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">{t('validators.aiScore')}</p>
                  </div>
                </div>

                {/* 주요 정보 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('validators.totalStaking')}</span>
                    <span className="font-medium text-gray-900">{formatNumber(validator.totalStake)} VTTN</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">일일 보상:</span>
                    <span className="font-medium text-green-600">{formatNumber(validator.rewards24h)} VTTN</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">생성 블록:</span>
                    <span className="font-medium text-gray-900">{validator.blocks.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">위임자 수:</span>
                    <span className="font-medium text-gray-900">{validator.delegators.toLocaleString()}</span>
                  </div>
                </div>

                {/* 액션 버튼 */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedValidator(validator);
                      setShowDetailModal(true);
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span>상세 보기</span>
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        /* 리스트 뷰 - 기존 테이블 */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Stake</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uptime</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blocks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                        <span className="text-gray-500">Loading validators...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedValidators.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      No validators found
                    </td>
                  </tr>
                ) : (
                  paginatedValidators.map((validator) => (
                    <tr key={validator.nodeId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
                              <Shield className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              {validator.name ? (
                                <span className="text-sm font-medium text-gray-900">{validator.name}</span>
                              ) : (
                                <span className="text-sm font-medium text-gray-900 font-mono">
                                  {validator.nodeId.slice(0, 12)}...
                                </span>
                              )}
                              <button
                                onClick={() => copyToClipboard(validator.nodeId)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500 font-mono">
                                {formatAddress(validator.address)}
                              </span>
                              {validator.location && (
                                <span className="text-xs text-gray-400">{validator.location}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(validator.status)}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(validator.status)}`}>
                            {validator.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatNumber(validator.totalStake)} VTTN
                          </div>
                          <div className="text-xs text-gray-500">
                            Self: {formatNumber(validator.stake)} | Delegated: {formatNumber(validator.delegatedStake)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {validator.commission}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className={`text-sm font-medium ${getPerformanceColor(validator.uptime)}`}>
                            {validator.uptime.toFixed(1)}%
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                validator.uptime >= 95 ? 'bg-green-500' :
                                validator.uptime >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(validator.uptime, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className={`text-sm font-medium ${getPerformanceColor(validator.aiScore)}`}>
                            {validator.aiScore.toFixed(1)}
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                validator.aiScore >= 95 ? 'bg-green-500' :
                                validator.aiScore >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(validator.aiScore, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => navigate(`/blocks?validator=${validator.address}`)}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {validator.blocks.toLocaleString()}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                    onClick={() => {
                      fetchNodeDetail(validator.nodeId);
                    }}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 border rounded-lg ${
                  currentPage === page
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* 노드 상세 모달 - BSS 노드는 AIControlSystem 사용 */}
      {showDetailModal && selectedValidator && (
        selectedValidator.nodeId.startsWith('bss-') ? (
          <AIControlSystem
            stationId={selectedValidator.nodeId}
            stationName={selectedValidator.name || selectedValidator.nodeId}
            stationType="bss"
            onClose={() => {
              setShowDetailModal(false);
              setSelectedValidator(null);
            }}
          />
        ) : (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl mx-4 max-h-[95vh] overflow-hidden">
            {/* 헤더 */}
            <div className="bg-white border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedValidator.name || `${selectedValidator.nodeId.slice(0, 20)}...`}
                    </h3>
                    <p className="text-gray-500">노드 ID: {selectedValidator.nodeId}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {getStatusIcon(selectedValidator.status)}
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedValidator.status)}`}>
                        {selectedValidator.status}
                      </span>
                      <span className="text-sm text-gray-500">{selectedValidator.location}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* 콘텐츠 */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
              {/* 상태 카드들 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                  <Gauge className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                  <p className="text-sm text-gray-600">가동률</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedValidator.uptime.toFixed(1)}%</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm text-gray-600">일일 보상</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(selectedValidator.rewards24h)} VTTN</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                  <p className="text-sm text-gray-600">총 스테이킹</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(selectedValidator.totalStake)} VTTN</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                  <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                  <p className="text-sm text-gray-600">AI 점수</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedValidator.aiScore.toFixed(1)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 기본 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Server className="w-5 h-5 text-orange-500" />
                    <h4 className="text-lg font-semibold text-gray-900">기본 정보</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">지갑 주소:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900 font-mono text-sm">{formatAddress(selectedValidator.address)}</span>
                        <button
                          onClick={() => copyToClipboard(selectedValidator.address)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">수수료율:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.commission}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">위임자 수:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.delegators.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">버전:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.version}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">참여일:</span>
                      <span className="font-medium text-gray-900">{formatTimeAgo(selectedValidator.joinedAt)}</span>
                    </div>
                  </div>
                </div>

                {/* 블록체인 연동 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Network className="w-5 h-5 text-blue-500" />
                    <h4 className="text-lg font-semibold text-gray-900">블록체인 연동</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">네트워크:</span>
                      <span className="font-medium text-blue-600">Voltron Mainnet</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">체인 ID:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.networkInfo?.chainId || 88889}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">최신 블록:</span>
                      <button
                        onClick={() => navigate(`/blocks/${selectedValidator.networkInfo?.latestBlock || 2847123}`)}
                        className="font-medium text-blue-600 hover:text-blue-800 underline"
                      >
                        #{(selectedValidator.networkInfo?.latestBlock || 2847123).toLocaleString()}
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">동기화 상태:</span>
                      <span className="font-medium text-green-600">{selectedValidator.networkInfo?.syncStatus || 100}% 동기화됨</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">피어 연결:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.networkInfo?.peerCount || 47}개 노드</span>
                    </div>
                  </div>
                </div>

                {/* 수익 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    <h4 className="text-lg font-semibold text-gray-900">수익 정보</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">총 가스비 수익:</span>
                      <span className="font-medium text-green-600">${(selectedValidator.earnings?.totalGasFees || 15678.90).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">일일 가스비:</span>
                      <span className="font-medium text-gray-900">${(selectedValidator.earnings?.dailyGasFees || 125.50).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">스테이킹 보상:</span>
                      <span className="font-medium text-gray-900">${(selectedValidator.earnings?.stakingRewards || 2340.80).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">검증자 보상:</span>
                      <span className="font-medium text-gray-900">${(selectedValidator.earnings?.validatorRewards || 1890.60).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">월간 예상 수익:</span>
                      <span className="font-medium text-green-600 text-lg">${(selectedValidator.earnings?.monthlyGasFees || 3756.20).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* 하드웨어 사양 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <HardDrive className="w-5 h-5 text-gray-500" />
                    <h4 className="text-lg font-semibold text-gray-900">하드웨어 사양</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.specs?.cpu || '8 vCPU'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.specs?.ram || '32 GB'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">스토리지:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.specs?.storage || '1 TB SSD'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">대역폭:</span>
                      <span className="font-medium text-gray-900">{selectedValidator.specs?.bandwidth || '10 Gbps'}</span>
                    </div>
                  </div>
                </div>

                {/* 최근 생성 블록 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="w-5 h-5 text-purple-500" />
                      <h4 className="text-lg font-semibold text-gray-900">최근 생성 블록</h4>
                    </div>
                    <button
                      onClick={() => navigate(`/blocks?validator=${selectedValidator.address}`)}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      전체 보기
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(selectedValidator.recentBlocks || [
                      { blockNumber: 2847123, timestamp: '2분 전', transactions: 156, gasUsed: '8.5M', reward: '2.5 VTTN' },
                      { blockNumber: 2847122, timestamp: '4분 전', transactions: 142, gasUsed: '7.8M', reward: '2.3 VTTN' },
                      { blockNumber: 2847121, timestamp: '6분 전', transactions: 189, gasUsed: '9.2M', reward: '2.8 VTTN' }
                    ]).map((block, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <button
                            onClick={() => navigate(`/blocks/${block.blockNumber}`)}
                            className="font-medium text-blue-600 hover:text-blue-800 underline"
                          >
                            #{block.blockNumber.toLocaleString()}
                          </button>
                          <p className="text-sm text-gray-500">{block.timestamp}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{block.transactions} txs</p>
                          <p className="text-sm text-green-600">{block.reward}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 최근 처리 트랜잭션 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-5 h-5 text-indigo-500" />
                      <h4 className="text-lg font-semibold text-gray-900">최근 처리 트랜잭션</h4>
                    </div>
                    <button
                      onClick={() => navigate(`/transactions?validator=${selectedValidator.address}`)}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      전체 보기
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(selectedValidator.recentTransactions || [
                      { hash: '0x8f2a...9b4c', timestamp: '2분 전', value: '0.025 VTTN', gasPrice: '20 gwei' },
                      { hash: '0x7e1b...8a3d', timestamp: '5분 전', value: '0.031 VTTN', gasPrice: '22 gwei' },
                      { hash: '0x6d0c...7f2e', timestamp: '8분 전', value: '0.019 VTTN', gasPrice: '18 gwei' }
                    ]).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <button
                            onClick={() => navigate(`/transactions/${tx.hash}`)}
                            className="font-mono text-sm text-blue-600 hover:text-blue-800 underline"
                          >
                            {tx.hash}
                          </button>
                          <p className="text-sm text-gray-500">{tx.timestamp}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-green-600">{tx.value}</p>
                          <p className="text-sm text-gray-500">{tx.gasPrice}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                닫기
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => window.open(`/blocks?validator=${selectedValidator.address}`, '_blank')}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  생성 블록 보기
                </button>
                <button
                  onClick={() => window.open(`/transactions?validator=${selectedValidator.address}`, '_blank')}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  처리 트랜잭션 보기
                </button>
                <button
                  onClick={() => window.open('/validators', '_blank')}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  검증자 목록
                </button>
                <button
                  onClick={() => copyToClipboard(selectedValidator.address)}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  주소 복사
                </button>
              </div>
            </div>
          </div>
        </div>
        )
      )}

    </motion.div>
  );
};

export default ValidatorsPage;
